import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getBulkUploadPipeline } from '../service/bulkUpload.api';

// Which Bulk Upload pipeline a draft is on (build doc 06 §2, §7.1).
//
// The pin lives on the server (06 D1): a draft with a BULK ingestion job is v2,
// one with a v1 snapshot or batch is v1, anything else follows the teacher's
// flag. The wizard asks once per assignment (`staleTime: 0`, never
// createGlobalState) and the queryFn never resolves null.
//
// Unknown is never guessed for a draft that exists: a wrong guess could split
// one draft across both pipelines, so an error on an existing assignment is
// shown with Retry. Only a draft that doesn't exist yet (nothing can be pinned)
// falls back to v1. A 404 is an answer only when the server says what it means:
// 404 ASSIGNMENT_NOT_FOUND (the caller can't touch the assignment; the v1 flow
// then reports that itself) reads as v1, unpinned. Any other 404 on an existing
// draft (an edge or proxy 404) is an unknown like any other error: Retry.
//
// One exception: a backend that predates the resolver (the UI deploys on merge,
// the backend's traffic moves by hand and can be rolled back) has no such
// route, and Spring answers its own default 404 body {status: 404, error, path}
// with no `code`, the path ending in /bulk-upload/pipeline. That backend has no
// v2 pins and no ingestion routes, so v1 is the only pipeline that can work:
// read it as v1, unpinned, instead of blocking every existing v1 draft.

export const PIPELINE = Object.freeze({ V1: 'v1', V2: 'v2' });

export const V1_RESOLUTION = Object.freeze({
  pipeline: PIPELINE.V1,
  pinned: false,
  reason: 'FLAG_DEFAULT',
  ingestionJobId: null,
  autoSort: true,
});

/** What the hook reports while the flag sits at v1 (and in suites that don't test v2). */
export const V1_PIPELINE_STATE = Object.freeze({
  status: 'ready',
  pipeline: PIPELINE.V1,
  isV2: false,
  resolution: V1_RESOLUTION,
  fallback: false,
  error: null,
  retry: () => {},
});

export const bulkUploadPipelineKey = (assignmentId, client = 'web') => [
  'bulkUploadPipeline',
  assignmentId ?? 'new',
  client,
];

/** The server reply as a resolution; throws on a body that isn't one. */
export const toResolution = (body) => {
  const pipeline = String(body?.pipeline || '').toLowerCase();
  if (pipeline !== PIPELINE.V1 && pipeline !== PIPELINE.V2) {
    throw new Error("We couldn't tell which upload pipeline this draft uses.");
  }
  return {
    pipeline,
    pinned: Boolean(body.pinned),
    reason: body.reason || null,
    ingestionJobId: body.ingestionJobId || null,
    autoSort: body.autoSort !== false,
  };
};

/** Spring's default 404 for a route this backend doesn't have. */
export const isMissingPipelineRoute = (error) => {
  const body = error?.body;
  return (
    error?.status === 404 &&
    !error?.code &&
    body?.status === 404 &&
    !body?.code &&
    typeof body?.error === 'string' &&
    /\/bulk-upload\/pipeline\/?$/.test(String(body?.path || ''))
  );
};

export const fetchPipeline = async (assignmentId, client, signal) => {
  try {
    const body = await getBulkUploadPipeline(
      { assignmentId, client },
      { signal }
    );
    return toResolution(body);
  } catch (error) {
    if (
      error?.status === 404 &&
      (!assignmentId ||
        error?.code === 'ASSIGNMENT_NOT_FOUND' ||
        isMissingPipelineRoute(error))
    )
      return { ...V1_RESOLUTION, reason: 'UNAVAILABLE' };
    throw error;
  }
};

/**
 * @param {string|null} assignmentId  the draft or task; null before a draft exists
 * @param {{client?: 'web'|'mobile', enabled?: boolean}} [opts]
 * @returns {{status: 'loading'|'ready'|'error', pipeline: 'v1'|'v2'|null,
 *   isV2: boolean, resolution: object|null, fallback: boolean, error, retry}}
 */
export default function useBulkUploadPipeline(
  assignmentId,
  { client = 'web', enabled = true } = {}
) {
  const query = useQuery({
    queryKey: bulkUploadPipelineKey(assignmentId, client),
    queryFn: ({ signal }) => fetchPipeline(assignmentId, client, signal),
    enabled,
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: 1,
  });
  const { refetch } = query;
  const retry = useCallback(() => refetch(), [refetch]);

  if (query.data) {
    return {
      status: 'ready',
      pipeline: query.data.pipeline,
      isV2: query.data.pipeline === PIPELINE.V2,
      resolution: query.data,
      fallback: false,
      error: null,
      retry,
    };
  }
  if (query.isError) {
    if (!assignmentId) {
      return {
        status: 'ready',
        pipeline: PIPELINE.V1,
        isV2: false,
        resolution: V1_RESOLUTION,
        fallback: true,
        error: query.error,
        retry,
      };
    }
    return {
      status: 'error',
      pipeline: null,
      isV2: false,
      resolution: null,
      fallback: false,
      error: query.error,
      retry,
    };
  }
  return {
    status: 'loading',
    pipeline: null,
    isV2: false,
    resolution: null,
    fallback: false,
    error: null,
    retry,
  };
}
